/**
 * アプリケーション設定サービス
 *
 * localStorageを使用してアプリケーション全体の設定を永続化します。
 */

import logger from '../utils/logger';

const STORAGE_KEY = 'MIDAS_SETTINGS';
const CURRENT_VERSION = 2;

/**
 * グラフ設定の型定義
 */
export interface GraphSettings {
  maxFacetCategories: number;  // Facetの最大カテゴリ数
}

/**
 * デフォルトのグラフ設定
 */
export const DEFAULT_GRAPH_SETTINGS: GraphSettings = {
  maxFacetCategories: 20,
};

/**
 * アプリケーション設定の型定義
 */
export interface AppSettings {
  version: number;
  security: {
    trustedUrls: string[];
  };
  graph: GraphSettings;
  // 将来の拡張用
  // general: { ... }
  // appearance: { theme: 'light' | 'dark' }
}

/**
 * デフォルト設定
 */
const DEFAULT_SETTINGS: AppSettings = {
  version: CURRENT_VERSION,
  security: {
    trustedUrls: [],
  },
  graph: { ...DEFAULT_GRAPH_SETTINGS },
};

/**
 * 設定のマイグレーション
 * 古いバージョンの設定を新しいバージョンに変換
 */
function migrateSettings(settings: unknown): AppSettings {
  if (!settings || typeof settings !== 'object') {
    return { ...DEFAULT_SETTINGS };
  }

  const s = settings as Record<string, unknown>;
  let version = typeof s.version === 'number' ? s.version : 0;

  // バージョン0または不明: デフォルト設定を使用
  if (version < 1) {
    logger.info('[settingsService] Migrating settings from version', version, 'to', CURRENT_VERSION);
    return { ...DEFAULT_SETTINGS };
  }

  // バージョン1から2へのマイグレーション: graph設定を追加
  if (version === 1) {
    logger.info('[settingsService] Migrating settings from version 1 to 2');
    (s as Record<string, unknown>).graph = { ...DEFAULT_GRAPH_SETTINGS };
    (s as Record<string, unknown>).version = 2;
    version = 2;
  }

  // 現在のバージョン: そのまま使用
  if (version === CURRENT_VERSION) {
    return settings as AppSettings;
  }

  // 将来のバージョン用マイグレーション
  // if (version === 2) { ... migrate to version 3 ... }

  return settings as AppSettings;
}

/**
 * 設定を読み込む
 */
export function loadSettings(): AppSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      logger.debug('[settingsService] No stored settings found, using defaults');
      return { ...DEFAULT_SETTINGS };
    }

    const parsed = JSON.parse(stored);
    const migrated = migrateSettings(parsed);

    // マイグレーションが発生した場合は保存
    if (parsed.version !== migrated.version) {
      saveSettings(migrated);
    }

    return migrated;
  } catch (error) {
    logger.error('[settingsService] Failed to load settings:', error);
    return { ...DEFAULT_SETTINGS };
  }
}

/**
 * 設定を保存
 */
export function saveSettings(settings: AppSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    logger.debug('[settingsService] Settings saved');
  } catch (error) {
    logger.error('[settingsService] Failed to save settings:', error);
  }
}

/**
 * 信頼URLを追加
 */
export function addTrustedUrl(url: string): AppSettings {
  const settings = loadSettings();
  if (!settings.security.trustedUrls.includes(url)) {
    settings.security.trustedUrls.push(url);
    saveSettings(settings);
    logger.info('[settingsService] Added trusted URL:', url);
  }
  return settings;
}

/**
 * 信頼URLを削除
 */
export function removeTrustedUrl(url: string): AppSettings {
  const settings = loadSettings();
  const index = settings.security.trustedUrls.indexOf(url);
  if (index > -1) {
    settings.security.trustedUrls.splice(index, 1);
    saveSettings(settings);
    logger.info('[settingsService] Removed trusted URL:', url);
  }
  return settings;
}

/**
 * 設定をリセット
 */
export function resetSettings(): AppSettings {
  const settings = { ...DEFAULT_SETTINGS };
  saveSettings(settings);
  logger.info('[settingsService] Settings reset to defaults');
  return settings;
}

/**
 * グラフ設定を更新
 */
export function updateGraphSettings(graphSettings: Partial<GraphSettings>): AppSettings {
  const settings = loadSettings();
  settings.graph = { ...settings.graph, ...graphSettings };
  saveSettings(settings);
  logger.info('[settingsService] Graph settings updated:', graphSettings);
  return settings;
}
